import { useEffect, useEffectEvent, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.css';

interface IScreenshotLightboxProps {
  src: string;
  onClose: () => void;
}

/**
 * A Help Center screenshot at full size, over everything. Closes on a click
 * anywhere or Escape. Used by the guide reader and by assistant answers that
 * show a guide screenshot, so a screenshot zooms the same way wherever it is.
 */
export function ScreenshotLightbox({ src, onClose }: IScreenshotLightboxProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const onKey = useEffectEvent((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  });
  useEffect(() => {
    ref.current!.focus();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []); // onKey is stable via useEffectEvent; subscribe once per open lightbox
  // Portalled to <body>: inside a page it is trapped in the content pane's
  // stacking context and the sidebar paints over it.
  return createPortal(
    <div ref={ref} tabIndex={-1} role="dialog" aria-modal="true" aria-label={t('help.screenshot')} className={styles.lightbox} onClick={onClose}>
      <img src={src} alt="" />
    </div>,
    document.body,
  );
}
